import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { PLATFORM } from "../env";
import { useAuthApi } from "../hooks/useAuthApi";
import type { PlatformSettings } from "../types";

export default function Settings() {
  const authApi = useAuthApi();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [daysValue, setDaysValue] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await authApi<PlatformSettings>(`${PLATFORM}/settings`);
      if (data) {
        setSettings(data);
        setDaysValue(String(data.driver_additional_email_reverification_days));
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [authApi]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    const parsed = Number.parseInt(daysValue, 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      setError("Re-verification days must be at least 1.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const data = await authApi<PlatformSettings>(`${PLATFORM}/settings`, {
        method: "PATCH",
        body: JSON.stringify({
          driver_additional_email_reverification_days: parsed,
        }),
      });
      if (data) {
        setSettings(data);
        setDaysValue(String(data.driver_additional_email_reverification_days));
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Driver additional email verification
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Verified additional driver emails expire after this many days and then require a new OTP verification.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!settings && !error && <Alert severity="warning" sx={{ mb: 2 }}>No settings loaded.</Alert>}

      <TextField
        label="Re-verification interval (days)"
        type="number"
        value={daysValue}
        onChange={(e) => setDaysValue(e.target.value)}
        fullWidth
        inputProps={{ min: 1 }}
      />

      <Box sx={{ mt: 2 }}>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </Box>
    </Box>
  );
}
